// Name normalization + the "already exists on this site" duplicate screen.
//
// Deterministic, no AI: candidates are compared by normalized name against
// the site's existing custom fields (same fieldset, pre-filtered by the
// caller). Exact normalized matches present as "already exists — skip";
// containment matches present as "similar — review". Nothing is ever
// silently created over a match. (Fuzzier similarity is a deliberate
// increment-2 item.)
//
// Pure module: no React, no Supabase, no fetch.

// Lowercase; strip leading numbering ("1.", "Q3:", "(2)"); spell out '#' and
// '&'; drop punctuation; collapse whitespace; singularize a simple trailing
// 's' per word ("Rooms" → "room", but "address"/"status" keep their 'ss').
export function normalizeFieldName(name) {
  if (name === null || name === undefined) return '';
  let s = String(name).toLowerCase().trim();
  s = s.replace(/^[\s(]*(?:q(?:uestion)?\s*)?\d+[\s.):\-]+/i, ''); // leading numbering
  s = s.replace(/[’'`]/g, '');                                    // "Today's" → "todays"
  s = s.replace(/#/g, ' number ');
  s = s.replace(/&/g, ' and ');
  s = s.replace(/[^a-z0-9\s]/g, ' ');
  const words = s.split(/\s+/).filter(Boolean).map(w =>
    w.length > 3 && w.endsWith('s') && !w.endsWith('ss') ? w.slice(0, -1) : w
  );
  return words.join(' ');
}

// Screen one candidate name against a list of existing custom fields
// (read-model rows from listCustomFields; anything with a `name`). Returns
//   { verdict: 'duplicate' | 'similar' | null, matches: [existingField…] }
// 'duplicate' = exact normalized-name match; 'similar' = one normalized name
// contains the other (both non-empty, not equal).
export function screenAgainstExisting(candidateName, existingFields) {
  const norm = normalizeFieldName(candidateName);
  if (!norm) return { verdict: null, matches: [] };
  const exact = [];
  const similar = [];
  for (const f of existingFields || []) {
    if (f?.isDeleted) continue;
    const other = normalizeFieldName(f?.name);
    if (!other) continue;
    if (other === norm) exact.push(f);
    else if (other.includes(norm) || norm.includes(other)) similar.push(f);
  }
  if (exact.length) return { verdict: 'duplicate', matches: exact };
  if (similar.length) return { verdict: 'similar', matches: similar };
  return { verdict: null, matches: [] };
}

// Batch-level duplicate check: candidate names must be unique within their
// fieldset (the API would otherwise create two same-named fields). Returns a
// Map of draftId → [conflicting draftIds].
export function findBatchNameCollisions(drafts) {
  const byKey = new Map();
  for (const d of drafts || []) {
    if (!d?.name) continue;
    const key = `${d.fieldset}::${normalizeFieldName(d.name)}`;
    if (!byKey.has(key)) byKey.set(key, []);
    byKey.get(key).push(d);
  }
  const collisions = new Map();
  for (const group of byKey.values()) {
    if (group.length < 2) continue;
    for (const d of group) {
      collisions.set(d.id, group.filter(x => x !== d).map(x => x.id));
    }
  }
  return collisions;
}
